import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputText } from 'app/components/elements/InputText';
import { DocumentationCategoryRow } from 'types/documents';
import {
  createDocumentationCategory,
  deleteDocumentationCategory,
  patchDocumentationCategory
} from 'services/documentation';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { ChangeEvent } from 'react';
import { useFlash } from 'contexts/flash';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const slugPattern = /^[A-Za-z][A-Za-z0-9_]*$/;

/** Spaces and hyphens become underscores (natural typing for category names). */
function normalizeDocumentationSlug(raw: string): string {
  return raw.trim().replace(/\s+/g, '_').replace(/-/g, '_').replace(/_+/g, '_');
}

type ModalManageDocumentationCategoriesProps = {
  open: boolean;
  onClose: () => void;
  categories: DocumentationCategoryRow[];
  onReload: () => Promise<void>;
};

function sortOrderForAppend(list: DocumentationCategoryRow[]) {
  if (list.length === 0) return 0;
  return Math.max(...list.map((c) => c.sortOrder)) + 1;
}

function SortableCategoryRow({
  row,
  onDelete,
  disabled,
  editingId,
  editSlug,
  onEditSlugChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit
}: {
  row: DocumentationCategoryRow;
  onDelete: (row: DocumentationCategoryRow) => void;
  disabled: boolean;
  editingId: string | null;
  editSlug: string;
  onEditSlugChange: (v: string) => void;
  onStartEdit: (row: DocumentationCategoryRow) => void;
  onCancelEdit: () => void;
  onSaveEdit: (row: DocumentationCategoryRow) => void;
}) {
  const editing = editingId === row.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: disabled || editing
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    ...(isDragging ? { backgroundColor: 'rgba(34,106,170,0.06)' as const } : {})
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell align="center" sx={{ width: 44, py: 0.5 }}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          aria-label="Drag to reorder"
          disabled={disabled || editing}
          sx={{
            cursor: disabled || editing ? 'default' : 'grab',
            '&:active': { cursor: 'grabbing' }
          }}>
          <DragIndicatorIcon fontSize="small" sx={{ color: '#226AAA' }} />
        </IconButton>
      </TableCell>
      <TableCell>
        {editing ? (
          <InputText
            title=""
            placeholder="Enter name"
            inputProps={{
              value: editSlug,
              onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                onEditSlugChange(e.target.value),
              autoFocus: true,
              style: { maxWidth: 360 }
            }}
          />
        ) : (
          <>
            <Typography>{row.slug.replace(/_/g, ' ')}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {row.slug}
            </Typography>
          </>
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {!disabled && (
          <Stack flexDirection="row" gap={1} justifyContent="flex-end">
            {editing ? (
              <>
                <ButtonCustom
                  label="Save"
                  onClick={() => onSaveEdit(row)}
                  style={{ minWidth: 72, height: 36 }}
                />
                <ButtonCustom
                  label="Cancel"
                  onClick={onCancelEdit}
                  style={{ minWidth: 72, height: 36 }}
                />
              </>
            ) : (
              <>
                <ButtonCustom
                  label="Edit"
                  onClick={() => onStartEdit(row)}
                  style={{ minWidth: 72, height: 36 }}
                />
                <ButtonCustom
                  isDelete
                  label="Delete"
                  onClick={() => onDelete(row)}
                  style={{ minWidth: 72, height: 36 }}
                />
              </>
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

export const ModalManageDocumentationCategories = ({
  open,
  onClose,
  categories,
  onReload
}: ModalManageDocumentationCategoriesProps) => {
  const { setFlash } = useFlash();
  const [newSlug, setNewSlug] = useState('');
  const [ordered, setOrdered] = useState<DocumentationCategoryRow[]>([]);
  const [reordering, setReordering] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSlug, setEditSlug] = useState('');
  const [categoryPendingDelete, setCategoryPendingDelete] =
    useState<DocumentationCategoryRow | null>(null);
  const [deletingCategory, setDeletingCategory] = useState(false);

  useEffect(() => {
    setOrdered([...categories].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [categories, open]);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditSlug('');
      setCategoryPendingDelete(null);
    }
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const resetAdd = useCallback(() => {
    setNewSlug('');
  }, []);

  const handleCreate = useCallback(async () => {
    const slug = normalizeDocumentationSlug(newSlug);
    if (!slugPattern.test(slug)) {
      setFlash({
        type: 'error',
        message:
          'Use a name starting with a letter. Letters, numbers, spaces, hyphens, and underscores are ok (spaces become underscores).'
      });
      return;
    }
    const sortOrder = sortOrderForAppend(ordered);
    try {
      await createDocumentationCategory({ slug, sortOrder });
      setFlash({ type: 'success', message: 'Category created' });
      resetAdd();
      await onReload();
    } catch {
      setFlash({ type: 'error', message: 'Could not create category' });
    }
  }, [newSlug, ordered, onReload, resetAdd, setFlash]);

  const handleDelete = useCallback((row: DocumentationCategoryRow) => {
    setCategoryPendingDelete(row);
  }, []);

  const handleConfirmCategoryDelete = useCallback(async () => {
    const row = categoryPendingDelete;
    if (!row) return;
    setDeletingCategory(true);
    try {
      await deleteDocumentationCategory(Number(row.id));
      setFlash({ type: 'success', message: 'Category deleted' });
      setCategoryPendingDelete(null);
      setEditingId(null);
      await onReload();
    } catch {
      setFlash({ type: 'error', message: 'Could not delete category' });
    } finally {
      setDeletingCategory(false);
    }
  }, [categoryPendingDelete, onReload, setFlash]);

  const handleCloseDeleteDialog = useCallback(() => {
    if (!deletingCategory) {
      setCategoryPendingDelete(null);
    }
  }, [deletingCategory]);

  const handleStartEdit = useCallback((row: DocumentationCategoryRow) => {
    setEditingId(row.id);
    setEditSlug(row.slug);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditSlug('');
  }, []);

  const handleSaveEdit = useCallback(
    async (row: DocumentationCategoryRow) => {
      const slug = normalizeDocumentationSlug(editSlug);
      if (!slugPattern.test(slug)) {
        setFlash({
          type: 'error',
          message:
            'Use a name starting with a letter. Letters, numbers, spaces, hyphens, and underscores are ok (spaces become underscores).'
        });
        return;
      }
      if (slug === row.slug) {
        handleCancelEdit();
        return;
      }
      try {
        await patchDocumentationCategory(Number(row.id), { slug });
        setFlash({ type: 'success', message: 'Category updated' });
        setEditingId(null);
        setEditSlug('');
        await onReload();
      } catch {
        setFlash({ type: 'error', message: 'Could not update name (is it taken?)' });
      }
    },
    [editSlug, onReload, setFlash, handleCancelEdit]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || reordering) return;

      const oldIndex = ordered.findIndex((r) => String(r.id) === String(active.id));
      const newIndex = ordered.findIndex((r) => String(r.id) === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(ordered, oldIndex, newIndex);
      const toPatch = next
        .map((row, idx) => (row.sortOrder !== idx ? { row, idx } : null))
        .filter((x): x is { row: DocumentationCategoryRow; idx: number } => x !== null);

      if (!toPatch.length) {
        setOrdered(next.map((row, idx) => ({ ...row, sortOrder: idx })));
        return;
      }

      setReordering(true);
      setOrdered(next.map((row, idx) => ({ ...row, sortOrder: idx })));

      try {
        await Promise.all(
          toPatch.map(({ row, idx }) =>
            patchDocumentationCategory(Number(row.id), { sortOrder: idx })
          )
        );
        setFlash({ type: 'success', message: 'Order updated' });
        await onReload();
      } catch {
        setFlash({ type: 'error', message: 'Could not reorder categories' });
        await onReload();
      } finally {
        setReordering(false);
      }
    },
    [ordered, reordering, onReload, setFlash]
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Typography fontSize={20} fontWeight={700} color="#226AAA">
              Documentation categories
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="#666" mb={2}>
            You can type names with spaces (they are saved with underscores). New categories go to
            the bottom; drag rows to reorder. Only Admin / Super Admin can change this list.
          </Typography>
          <Stack gap={2} mb={3}>
            <Typography fontWeight={600}>Add category</Typography>
            <Stack flexDirection="row" gap={1.5} flexWrap="wrap" alignItems="flex-end">
              <InputText
                title="Name"
                placeholder="e.g. My Category"
                inputProps={{
                  value: newSlug,
                  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    setNewSlug(e.target.value)
                }}
              />
              <ButtonCustom label="Add" onClick={handleCreate} style={{ width: 120, height: 46 }} />
            </Stack>
          </Stack>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 52 }} aria-hidden />
                  <TableCell>Name</TableCell>
                  <TableCell width={260} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SortableContext
                  items={ordered.map((r) => String(r.id))}
                  strategy={verticalListSortingStrategy}>
                  {ordered.map((row) => (
                    <SortableCategoryRow
                      key={row.id}
                      row={row}
                      onDelete={handleDelete}
                      disabled={reordering}
                      editingId={editingId}
                      editSlug={editSlug}
                      onEditSlugChange={setEditSlug}
                      onStartEdit={handleStartEdit}
                      onCancelEdit={handleCancelEdit}
                      onSaveEdit={handleSaveEdit}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        </DialogContent>
      </Dialog>

      <Dialog
        open={categoryPendingDelete !== null}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="delete-category-dialog-title">
        <DialogTitle id="delete-category-dialog-title">Delete category?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" fontSize={14} color="text.primary" component="div" mb={1.5}>
            You are about to remove{' '}
            <strong>{categoryPendingDelete?.slug.replace(/_/g, ' ')}</strong> (
            <Box component="span" sx={{ typography: 'caption', color: 'text.secondary' }}>
              {categoryPendingDelete?.slug}
            </Box>
            ) from this list.
          </Typography>
          <Typography variant="body2" fontSize={14} color="text.secondary" component="div">
            If you confirm, <strong>all documentation files under this folder</strong> will be
            hidden from the Documentation library (soft-deleted), together with this category.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <ButtonCustom
            label="Cancel"
            onClick={handleCloseDeleteDialog}
            disabled={deletingCategory}
            style={{ minWidth: 100, height: 40 }}
          />
          <ButtonCustom
            isDelete
            label={deletingCategory ? 'Deleting…' : 'Delete category'}
            onClick={handleConfirmCategoryDelete}
            disabled={deletingCategory}
            style={{ minWidth: 160, height: 40 }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
